import { Link } from "react-router-dom";
import {
  IconTarget,
  IconCamera,
  IconSpeakerphone,
  IconBrowser,
  IconCpu,
  IconChartBar,
} from "@tabler/icons-react";
import { ArrowRight } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { SectionLabel } from "@/components/SectionLabel";
import { GlowCard } from "@/components/ui/spotlight-card";

const capabilities = [
  {
    slug: "strategy-brand",
    Icon: IconTarget,
    title: "Strategy & Brand",
    description:
      "We analyze your business, market, competitors and customers to build your positioning, offer and growth plan.",
  },
  {
    slug: "creative-content",
    Icon: IconCamera,
    title: "Creative & Content",
    description:
      "We create the concepts, videos, images, copy and ads that get your brand noticed.",
  },
  {
    slug: "media-acquisition",
    Icon: IconSpeakerphone,
    title: "Media & Acquisition",
    description:
      "We launch and manage advertising campaigns that turn attention into leads, calls, appointments and sales.",
  },
  {
    slug: "digital-experience",
    Icon: IconBrowser,
    title: "Digital Experience",
    description:
      "Websites, landing pages and conversion funnels built so your traffic never gets lost.",
  },
  {
    slug: "ai-automation",
    Icon: IconCpu,
    title: "AI & Automation",
    description:
      "We use AI to produce faster, generate multiple ad variations, and automate follow-up and processes.",
  },
  {
    slug: "analytics-growth",
    Icon: IconChartBar,
    title: "Analytics & Growth",
    description:
      "We track investment, leads, cost-per-lead and conversions, report clearly, and scale what's working.",
  },
];

export function SolutionsSection() {
  return (
    <section data-testid="solutions-section" className="py-28 md:py-36 border-t border-white/10 bg-black">
      <div className="container-custom">
        <FadeIn className="mb-12">
          <SectionLabel>What We Do</SectionLabel>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white max-w-2xl">
            ONE PARTNER.
            <br />
            EVERYTHING YOU NEED TO GROW.
          </h2>
        </FadeIn>

        <div
          data-testid="solutions-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {capabilities.map((cap, i) => (
            <FadeIn key={cap.slug} delay={Math.min(i * 0.06, 0.24)}>
              <GlowCard glowColor="purple" customSize={true} className="!h-full bg-black">
                <div data-testid={`capability-card-${cap.slug}`} className="flex flex-col h-full">
                  <cap.Icon size={32} className="text-violet-400" stroke={1.5} />
                  <h3 className="mt-5 font-display text-xl font-bold text-white leading-snug">
                    {cap.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed flex-1">
                    {cap.description}
                  </p>
                  <Link
                    to={`/services#${cap.slug}`}
                    data-testid={`capability-learn-more-${cap.slug}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-violet-400 hover:text-violet-300 transition-colors duration-300 group"
                  >
                    Learn More
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </GlowCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
